import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CreditCard, Fingerprint, Search, UserPlus, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axiosConfig';
import { Avatar, Cargando, botonAzul, botonClaro, campo, display, panel } from '../asistencia/ui';
import FichaPersona from './FichaPersona';
import { Encabezado, Vacio, Ventana } from './ui';
import { fecha } from './comun';

const sinTildes = (t) => (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const abreAlgo = (p) => p.tarjetas.some((t) => t.abre);

const FILTROS = [
    { id: 'todas', label: 'Todas', cumple: () => true },
    { id: 'abren', label: 'Pueden entrar', cumple: abreAlgo },
    { id: 'no_abren', label: 'No abren nada', cumple: (p) => !abreAlgo(p) },
    { id: 'sin_huella', label: 'Sin huella', cumple: (p) => !p.huellas },
    { id: 'fuera', label: 'No están en Centro SURA', cumple: (p) => !p.empleado },
    { id: 'egresados', label: 'Egresados', cumple: (p) => p.empleado && !p.empleado.activo },
];

const botonPeligro = 'h-9 px-3 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 border border-rose-200 text-rose-700 bg-white hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 disabled:opacity-40';

const Casilla = ({ marcada, onCambio, etiqueta, mixta = false }) => (
    <input
        type="checkbox" checked={marcada} aria-label={etiqueta}
        ref={(el) => { if (el) el.indeterminate = mixta; }}
        onChange={(e) => onCambio(e.target.checked)} onClick={(e) => e.stopPropagation()}
        className="w-4 h-4 rounded border-slate-300 text-sura-blue focus:ring-cyan-400/60 cursor-pointer"
    />
);

// Buscar a alguien de Centro SURA que todavía no está en las puertas y darlo de alta, con
// una tarjeta del stock en el grupo elegido: queda listo para enrolarle la huella
const Alta = ({ grupos, onCerrar, onLista }) => {
    const [q, setQ] = useState('');
    const [grupo, setGrupo] = useState('');
    const [lista, setLista] = useState(null);
    const [enviando, setEnviando] = useState(null);

    useEffect(() => {
        let activo = true;
        const t = setTimeout(() => {
            axiosInstance.get('/api/puertas/personas/candidatos', { params: { q: q.trim() || undefined } })
                .then(({ data }) => activo && setLista(data.empleados))
                .catch(() => activo && setLista([]));
        }, 300);
        return () => { activo = false; clearTimeout(t); };
    }, [q]);

    const dar = async (emp) => {
        setEnviando(emp.id);
        try {
            const { data } = await axiosInstance.post('/api/puertas/personas', { empleado_id: emp.id, grupo_id: Number(grupo) });
            if (data.tarjeta) toast.success(`${emp.nombre} ya está en las puertas con la tarjeta ${data.tarjeta}. Falta su huella.`);
            else toast(`${emp.nombre} ya está en las puertas. ${data.aviso || 'Falta darle tarjeta.'}`);
            onLista(data.id);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo dar de alta');
            setEnviando(null);
        }
    };

    return (
        <Ventana titulo="Dar de alta en las puertas" onCerrar={onCerrar} ancho="max-w-lg" bloqueada={!!enviando}>
            <p className="text-sm mt-1.5 text-slate-500">
                Se crea en GeoVision con los datos de su ficha y se le asigna una tarjeta libre, que es a la que se amarra su huella. Después le enrolas la huella.
            </p>
            <label className="block mt-4">
                <span className="text-xs text-slate-500">Qué puede abrir</span>
                <select value={grupo} onChange={(e) => setGrupo(e.target.value)} className={`${campo} w-full mt-1 pr-8`}>
                    <option value="" disabled>Elige su grupo de acceso</option>
                    {grupos.filter((g) => g.abre).map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                </select>
            </label>
            <label className="relative block mt-3">
                <span className="sr-only">Buscar empleado</span>
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre o cédula" className={`${campo} w-full pl-9`} />
            </label>
            <div className="mt-3 max-h-80 overflow-y-auto -mx-2">
                {!lista ? <Cargando alto="h-32" /> : lista.length === 0 ? (
                    <p className="px-2 py-6 text-sm text-center text-slate-400">Nadie por dar de alta con ese nombre.</p>
                ) : (
                    <ul>
                        {lista.map((emp) => (
                            <li key={emp.id} className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-slate-50">
                                <Avatar persona={emp} size={32} />
                                <span className="flex-1 min-w-0 leading-tight">
                                    <span className="block text-sm truncate text-slate-900">{emp.nombre}</span>
                                    <span className="block text-xs truncate text-slate-400">{[emp.departamento, emp.cedula && `Cédula ${emp.cedula}`].filter(Boolean).join(', ')}</span>
                                </span>
                                <button type="button" onClick={() => dar(emp)} disabled={!!enviando || grupo === ''} className={botonClaro}>
                                    {enviando === emp.id ? 'Dando de alta' : 'Dar de alta'}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Ventana>
    );
};

// Borra en GeoVision una lista, de a uno (como lo hace ASManager), y cuenta cómo le fue
const enLote = async (items, borrar, alAvanzar) => {
    const fallas = [];
    for (let i = 0; i < items.length; i++) {
        alAvanzar(i + 1);
        try {
            // eslint-disable-next-line no-await-in-loop
            await borrar(items[i]);
        } catch (e) {
            fallas.push({ item: items[i], error: e.response?.data?.error || 'falló' });
        }
    }
    return fallas;
};

const ConfirmarLote = ({ titulo, nombres, explicacion, accion, onConfirmar, onCerrar }) => {
    const [avance, setAvance] = useState(0);
    const corriendo = avance > 0;
    return (
        <Ventana titulo={titulo} onCerrar={onCerrar} bloqueada={corriendo}>
            <p className="text-sm mt-2 text-slate-500">{explicacion}</p>
            <ul className="mt-3 max-h-48 overflow-y-auto text-sm text-slate-700 space-y-0.5">
                {nombres.slice(0, 12).map((n) => <li key={n}>{n}</li>)}
                {nombres.length > 12 && <li className="text-slate-400">y {nombres.length - 12} más</li>}
            </ul>
            <div className="flex items-center justify-end gap-2 mt-6">
                {corriendo && <span className="mr-auto text-sm text-slate-500 tabular-nums">{avance} de {nombres.length}</span>}
                <button type="button" onClick={onCerrar} disabled={corriendo} className={botonClaro}>Cancelar</button>
                <button type="button" onClick={() => onConfirmar(setAvance)} disabled={corriendo} className={botonPeligro}>{accion}</button>
            </div>
        </Ventana>
    );
};

// Tarjetas que no son de nadie. No hay tarjetas físicas: son números a los que se amarran
// huellas, así que una sin dueño con grupo de acceso sobra
const TarjetasSinDueno = ({ tarjetas, puede, onCambio }) => {
    const [verTodas, setVerTodas] = useState(false);
    const [seleccion, setSeleccion] = useState(() => new Set());
    const [confirmar, setConfirmar] = useState(false);
    const abren = tarjetas.filter((t) => t.abre);
    const visibles = verTodas ? tarjetas : abren;
    const elegidas = tarjetas.filter((t) => seleccion.has(t.id));

    const marcar = (id, si) => setSeleccion((prev) => {
        const n = new Set(prev);
        if (si) n.add(id); else n.delete(id);
        return n;
    });

    const borrar = async (setAvance) => {
        const fallas = await enLote(elegidas, (t) => axiosInstance.delete(`/api/puertas/tarjetas/${t.id}`), setAvance);
        const hechas = elegidas.length - fallas.length;
        if (hechas) toast.success(hechas === 1 ? '1 tarjeta borrada' : `${hechas} tarjetas borradas`);
        if (fallas.length) toast.error(`No se pudieron borrar ${fallas.length}: ${fallas[0].error}`);
        setConfirmar(false);
        setSeleccion(new Set());
        onCambio();
    };

    if (!tarjetas.length) return null;
    return (
        <section className={`${panel} overflow-hidden`} aria-labelledby="sin-dueno-titulo">
            <header className="px-5 pt-5 pb-3 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 id="sin-dueno-titulo" className={`${display} font-bold text-slate-900 inline-flex items-center gap-2`}>
                        <CreditCard size={16} className="text-sura-blue" /> Tarjetas sin dueño
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        {tarjetas.length === 1 ? '1 tarjeta no es de nadie.' : `${tarjetas.length} tarjetas no son de nadie.`}
                        {abren.length > 0
                            ? <span className="text-amber-700 font-medium"> {abren.length === 1 ? '1 todavía tiene un grupo que abre puertas' : `${abren.length} todavía tienen un grupo que abre puertas`}: mejor bórralas.</span>
                            : ' Ninguna abre puertas.'}
                    </p>
                </div>
                {puede && elegidas.length > 0 && (
                    <button type="button" onClick={() => setConfirmar(true)} className={botonPeligro}>
                        Borrar {elegidas.length === 1 ? '1 tarjeta' : `${elegidas.length} tarjetas`}
                    </button>
                )}
            </header>
            {visibles.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-slate-500 border-y border-slate-100">
                                {puede && (
                                    <th className="pl-5 pr-1 py-2.5 w-8">
                                        <Casilla
                                            etiqueta="Elegir todas" marcada={visibles.every((t) => seleccion.has(t.id))}
                                            mixta={visibles.some((t) => seleccion.has(t.id)) && !visibles.every((t) => seleccion.has(t.id))}
                                            onCambio={(si) => visibles.forEach((t) => marcar(t.id, si))}
                                        />
                                    </th>
                                )}
                                <th className={`${puede ? 'px-3' : 'pl-5 pr-3'} py-2.5 font-medium`}>Tarjeta</th>
                                <th className="px-3 py-2.5 font-medium">Grupo de acceso</th>
                                <th className="pl-3 pr-5 py-2.5 font-medium whitespace-nowrap">Último uso</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {visibles.map((t) => (
                                <tr key={t.id} className="hover:bg-slate-50/60">
                                    {puede && (
                                        <td className="pl-5 pr-1 py-2">
                                            <Casilla etiqueta={`Elegir la tarjeta ${t.numero}`} marcada={seleccion.has(t.id)} onCambio={(si) => marcar(t.id, si)} />
                                        </td>
                                    )}
                                    <td className={`${puede ? 'px-3' : 'pl-5 pr-3'} py-2 tabular-nums ${t.activa ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{t.numero}</td>
                                    <td className="px-3 py-2">
                                        <span className="text-slate-600">{t.grupo}</span>
                                        {t.abre && <span className="ml-2 text-xs text-amber-700 font-medium">abre puertas</span>}
                                    </td>
                                    <td className="pl-3 pr-5 py-2 tabular-nums text-slate-500 whitespace-nowrap">{t.ultimo_acceso ? fecha(t.ultimo_acceso) : 'Nunca'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <div className="px-5 py-3">
                <button
                    type="button" onClick={() => setVerTodas((v) => !v)}
                    className="text-sm text-sura-blue hover:underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                >
                    {verTodas ? 'Ver solo las que abren' : `Ver las ${tarjetas.length - abren.length} que no abren nada`}
                </button>
            </div>
            {confirmar && (
                <ConfirmarLote
                    titulo={`¿Borrar ${elegidas.length === 1 ? 'esta tarjeta' : `estas ${elegidas.length} tarjetas`}?`}
                    nombres={elegidas.map((t) => `${t.numero} (${t.grupo})`)}
                    explicacion="Dejan de abrir de inmediato. Se borran de GeoVision."
                    accion="Borrar" onConfirmar={borrar} onCerrar={() => setConfirmar(false)}
                />
            )}
        </section>
    );
};

/**
 * Quién puede entrar: las personas de GeoVision con su tarjeta, su grupo de acceso y
 * cuántas huellas tienen. Tocar a alguien abre su ficha para cambiarle los permisos; para
 * depurar se eligen varias y se quitan de una vez.
 */
const PersonasVista = () => {
    const [datos, setDatos] = useState(null);
    const [error, setError] = useState(null);
    const [q, setQ] = useState('');
    const [filtro, setFiltro] = useState('todas');
    const [grupo, setGrupo] = useState('');
    const [abierta, setAbierta] = useState(null);
    const [alta, setAlta] = useState(false);
    const [seleccion, setSeleccion] = useState(() => new Set());
    const [confirmar, setConfirmar] = useState(false);

    const cargar = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get('/api/puertas/personas');
            setDatos(data);
            setError(null);
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudo cargar quién puede entrar');
        }
    }, []);
    useEffect(() => { cargar(); }, [cargar]);

    const personas = useMemo(() => {
        if (!datos) return [];
        const t = sinTildes(q.trim());
        const f = FILTROS.find((x) => x.id === filtro);
        return datos.personas.filter((p) => f.cumple(p)
            && (grupo === '' || p.tarjetas.some((tj) => String(tj.grupo_id) === grupo))
            && (!t || sinTildes(p.empleado?.nombre || p.nombre).includes(t) || (p.cedula || '').includes(t)));
    }, [datos, q, filtro, grupo]);

    const conteo = useMemo(() => Object.fromEntries(FILTROS.map((f) => [f.id, datos ? datos.personas.filter(f.cumple).length : 0])), [datos]);
    const puede = !!datos?.puede_gestionar;
    const cerrarFicha = useCallback(() => setAbierta(null), []);
    const nombreDe = (p) => p.empleado?.nombre || p.nombre;
    const elegidas = (datos?.personas || []).filter((p) => seleccion.has(p.id));

    const marcar = (id, si) => setSeleccion((prev) => {
        const n = new Set(prev);
        if (si) n.add(id); else n.delete(id);
        return n;
    });

    const quitar = async (setAvance) => {
        const fallas = await enLote(elegidas, (p) => axiosInstance.delete(`/api/puertas/personas/${p.id}`), setAvance);
        const hechas = elegidas.length - fallas.length;
        if (hechas) toast.success(hechas === 1 ? '1 persona quitada de las puertas' : `${hechas} personas quitadas de las puertas`);
        if (fallas.length) toast.error(`No se pudo con ${fallas.map((f) => nombreDe(f.item)).join(', ')}: ${fallas[0].error}`);
        setConfirmar(false);
        setSeleccion(new Set());
        cargar();
    };

    return (
        <div className="space-y-6">
            <Encabezado
                titulo="Personas"
                acciones={puede && <button type="button" onClick={() => setAlta(true)} className={botonAzul}><UserPlus size={16} /> Dar de alta</button>}
            >
                Quién puede entrar, con qué tarjeta y por qué puertas. El grupo de acceso de la tarjeta dice a qué puertas entra.
            </Encabezado>

            <div className="flex flex-wrap items-center gap-3">
                <label className="relative flex-1 min-w-[14rem] max-w-sm">
                    <span className="sr-only">Buscar</span>
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre o cédula" className={`${campo} w-full pl-9`} />
                </label>
                <label>
                    <span className="sr-only">Grupo de acceso</span>
                    <select value={grupo} onChange={(e) => setGrupo(e.target.value)} className={`${campo} pr-8`}>
                        <option value="">Todos los grupos</option>
                        {(datos?.grupos || []).map((g) => <option key={g.id} value={String(g.id)}>{g.nombre}</option>)}
                    </select>
                </label>
            </div>
            <div className="flex flex-wrap gap-1.5 -mt-3" role="radiogroup" aria-label="Filtrar">
                {FILTROS.map((f) => (
                    <button
                        key={f.id} type="button" role="radio" aria-checked={filtro === f.id} onClick={() => setFiltro(f.id)}
                        className={`h-8 px-3 rounded-full text-sm border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 ${
                            filtro === f.id ? 'bg-sura-blue border-sura-blue text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                    >
                        {f.label} <span className="tabular-nums opacity-70">{conteo[f.id]}</span>
                    </button>
                ))}
            </div>

            {error && <p className="text-sm text-rose-600">{error}</p>}

            {puede && elegidas.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl bg-white border border-slate-200 puertas-aparece">
                    <span className="text-sm text-slate-700">
                        <span className="font-medium tabular-nums">{elegidas.length}</span> {elegidas.length === 1 ? 'persona elegida' : 'personas elegidas'}
                    </span>
                    <button type="button" onClick={() => setSeleccion(new Set())} className="text-sm text-slate-500 hover:text-slate-800 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60">
                        Quitar la selección
                    </button>
                    <span className="flex-1" />
                    <button type="button" onClick={() => setConfirmar(true)} className={botonPeligro}>Quitar de las puertas</button>
                </div>
            )}

            <div className={`${panel} overflow-hidden`}>
                {!datos && !error ? <Cargando /> : personas.length === 0 ? (
                    <Vacio icono={Users} titulo="Nadie con estos filtros">Prueba con otro nombre, otro grupo u otro filtro.</Vacio>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                                    {puede && (
                                        <th className="pl-5 pr-1 py-3 w-8">
                                            <Casilla
                                                etiqueta="Elegir a todas las de la lista" marcada={personas.every((p) => seleccion.has(p.id))}
                                                mixta={personas.some((p) => seleccion.has(p.id)) && !personas.every((p) => seleccion.has(p.id))}
                                                onCambio={(si) => personas.forEach((p) => marcar(p.id, si))}
                                            />
                                        </th>
                                    )}
                                    <th className={`${puede ? 'px-3' : 'pl-5 pr-3'} py-3 font-medium`}>Persona</th>
                                    <th className="px-3 py-3 font-medium">Tarjeta y grupo de acceso</th>
                                    <th className="px-3 py-3 font-medium">Huellas</th>
                                    <th className="pl-3 pr-5 py-3 font-medium whitespace-nowrap">Último acceso</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {personas.map((p) => {
                                    const nombre = nombreDe(p);
                                    const egresado = p.empleado && !p.empleado.activo;
                                    return (
                                        <tr
                                            key={p.id} tabIndex={0} onClick={() => setAbierta(p.id)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setAbierta(p.id); } }}
                                            className={`cursor-pointer focus-visible:outline-none focus-visible:bg-cyan-50/60 ${seleccion.has(p.id) ? 'bg-cyan-50/50' : 'hover:bg-slate-50/70'}`}
                                        >
                                            {puede && (
                                                <td className="pl-5 pr-1 py-2.5">
                                                    <Casilla etiqueta={`Elegir a ${nombre}`} marcada={seleccion.has(p.id)} onCambio={(si) => marcar(p.id, si)} />
                                                </td>
                                            )}
                                            <td className={`${puede ? 'px-3' : 'pl-5 pr-3'} py-2.5`}>
                                                <span className="flex items-center gap-3 min-w-[13rem]">
                                                    <Avatar persona={{ nombre, foto: p.empleado?.foto }} size={32} />
                                                    <span className="min-w-0 leading-tight">
                                                        <span className="block truncate font-medium text-slate-900">{nombre}</span>
                                                        <span className={`block text-xs truncate ${egresado ? 'text-amber-700' : 'text-slate-400'}`}>
                                                            {egresado ? 'Egresó de SURA' : p.empleado?.departamento || p.departamento || (p.empleado ? 'Sin departamento' : 'No está en Centro SURA')}
                                                        </span>
                                                    </span>
                                                </span>
                                            </td>
                                            <td className="px-3 py-2.5">
                                                {p.tarjetas.length === 0 ? <span className="text-slate-400">Sin tarjeta</span> : (
                                                    <span className="flex flex-col gap-0.5">
                                                        {p.tarjetas.map((t) => (
                                                            <span key={t.id} className="whitespace-nowrap">
                                                                <span className={`tabular-nums ${t.activa ? 'text-slate-800' : 'text-slate-400 line-through'}`}>{t.numero}</span>
                                                                <span className={`ml-2 ${t.abre ? 'text-slate-600' : 'text-slate-400'}`}>{t.grupo}</span>
                                                                {!t.abre && <span className="ml-2 text-xs text-slate-400">no abre nada</span>}
                                                            </span>
                                                        ))}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5 whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-1.5 ${p.huellas ? 'text-sura-blue' : 'text-slate-400'}`}>
                                                    <Fingerprint size={15} /> {p.huellas || 'Ninguna'}
                                                </span>
                                            </td>
                                            <td className="pl-3 pr-5 py-2.5 whitespace-nowrap tabular-nums text-slate-600">
                                                {p.ultimo_acceso ? fecha(p.ultimo_acceso) : <span className="text-slate-400">Nunca</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {datos && <TarjetasSinDueno tarjetas={datos.tarjetas_libres || []} puede={puede} onCambio={cargar} />}

            {abierta != null && <FichaPersona personaId={abierta} puedeGestionar={puede} onCerrar={cerrarFicha} onCambio={cargar} />}
            {alta && <Alta grupos={datos?.grupos || []} onCerrar={() => setAlta(false)} onLista={(id) => { setAlta(false); cargar(); if (id) setAbierta(id); }} />}
            {confirmar && (
                <ConfirmarLote
                    titulo={`¿Quitar ${elegidas.length === 1 ? 'a esta persona' : `a estas ${elegidas.length} personas`} de las puertas?`}
                    nombres={elegidas.map(nombreDe)}
                    explicacion="Se borran de GeoVision con sus tarjetas y sus huellas: no van a poder entrar por ninguna puerta."
                    accion="Quitar de las puertas" onConfirmar={quitar} onCerrar={() => setConfirmar(false)}
                />
            )}
        </div>
    );
};

export default PersonasVista;
